import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart, Bar, XAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import { 
  FaBell, FaMapMarkerAlt, FaTruck, FaShieldAlt, 
  FaFilter, FaDownload, FaSyncAlt, FaCircle, FaChevronRight,
  FaFileExcel, FaFilePdf
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable'; // Import directly
import "react-datepicker/dist/react-datepicker.css";

const COLORS = {
  primary: '#3B82F6',
  teal: '#0D9488',
  purple: '#8B5CF6',
  green: '#22C55E',
  warning: '#F59E0B',
  danger: '#EF4444',
  slate: '#64748b'
};

const SEVERITY_COLORS = {
  Critical: '#EF4444',
  High: '#F59E0B',
  Medium: '#3B82F6',
  Low: '#94A3B8'
};

const AlertsMonitor = () => {
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date());
  const [filter, setFilter] = useState('All Alerts');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  // --- Data ---
  const allAlerts = [
    { id: 1, type: 'Missed Check-in', emp: 'Marcus Wright', time: '10 mins ago', sev: 'High', icon: <FaBell /> },
    { id: 2, type: 'No GPS Signal', emp: 'Vehicle #402', time: '24 mins ago', sev: 'Critical', icon: <FaMapMarkerAlt /> },
    { id: 3, type: 'Delayed Delivery', emp: 'Order #9921', time: '1 hour ago', sev: 'Medium', icon: <FaTruck /> },
    { id: 4, type: 'Warranty Breach', emp: 'HVAC Unit B1', time: '3 hours ago', sev: 'Critical', icon: <FaShieldAlt /> },
  ];

  const sitePerformanceData = [
    { name: 'Optimal', value: 65, fill: COLORS.teal },
    { name: 'Warning', value: 25, fill: COLORS.warning },
    { name: 'Critical', value: 10, fill: COLORS.danger },
  ];

  // --- Logic ---
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    
    // Close export menu on outside click
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filteredAlerts = filter === 'Critical Only' 
    ? allAlerts.filter(a => a.sev === 'Critical') 
    : allAlerts;

  const chartData = [
    { name: 'Missed', value: filteredAlerts.filter(a => a.type.includes('Check')).length, fill: COLORS.primary },
    { name: 'GPS', value: filteredAlerts.filter(a => a.type.includes('GPS')).length, fill: COLORS.teal },
    { name: 'Delivery', value: filteredAlerts.filter(a => a.type.includes('Delivery')).length, fill: COLORS.warning },
    { name: 'Warranty', value: filteredAlerts.filter(a => a.type.includes('Warranty')).length, fill: COLORS.danger },
  ];

  const handleExport = (format) => {
    setShowExportMenu(false);
    if (format === 'excel') {
      const ws = XLSX.utils.json_to_sheet(filteredAlerts.map(({id, icon, ...rest}) => rest));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Alerts");
      XLSX.writeFile(wb, `Alerts_Report_${filter.replace(/\s/g, '_')}.xlsx`);
    } else {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Alerts & Exceptions Report", 14, 22);
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text(`Filter applied: ${filter} | Date: ${startDate.toLocaleDateString()}`, 14, 30);
      
      // Fixed: Use the imported autoTable directly to avoid "is not a function" error
      autoTable(doc, {
        head: [['Type', 'Entity', 'Time', 'Severity']],
        body: filteredAlerts.map(a => [a.type, a.emp, a.time, a.sev]),
        startY: 40,
        theme: 'striped',
        headStyles: { fillStyle: COLORS.slate }
      });
      
      doc.save(`Alerts_Report_${filter.replace(/\s/g, '_')}.pdf`);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Roboto'] text-slate-700 p-8">
      <header className="flex justify-between items-center mb-10 pb-6 border-b border-slate-100">
        <div>
          <h1 className="font-['Poppins'] text-2xl font-bold text-slate-900 tracking-tight">Alerts & Exceptions</h1>
          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-1">Status: Online</p>
        </div>

        <div className="flex items-center gap-3 relative">
          <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
            <FaFilter className="text-slate-400 text-[10px]" />
            <select 
              className="bg-transparent text-xs font-bold outline-none cursor-pointer" 
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option>All Alerts</option>
              <option>Critical Only</option>
            </select>
          </div>
          <div className="bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100 flex items-center">
            <DatePicker selected={startDate} onChange={(d) => setStartDate(d)} className="bg-transparent text-xs font-bold w-24 outline-none" />
          </div>
          
          <div className="relative" ref={exportMenuRef}>
            <button 
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="bg-slate-900 text-white p-2.5 rounded-full hover:bg-primary transition-all active:scale-90"
            >
              <FaDownload size={12} />
            </button>
            <AnimatePresence>
              {showExportMenu && (
                <motion.div 
                  initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 mt-2 w-40 bg-white shadow-2xl rounded-xl border border-slate-100 z-50 overflow-hidden"
                >
                  <button onClick={() => handleExport('excel')} className="w-full flex items-center gap-3 px-4 py-3 text-[11px] font-bold hover:bg-emerald-50 text-emerald-700 transition-colors">
                    <FaFileExcel size={14}/> Excel (.xlsx)
                  </button>
                  <button onClick={() => handleExport('pdf')} className="w-full flex items-center gap-3 px-4 py-3 text-[11px] font-bold hover:bg-rose-50 text-rose-700 border-t border-slate-50 transition-colors">
                    <FaFilePdf size={14}/> PDF (.pdf)
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {loading ? <LoadingSkeleton /> : (
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
            <div className="h-64">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">Alerts Volume</h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700}} />
                  <Tooltip cursor={{fill: '#f8fafc'}} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="h-64">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">Site Health Index</h3>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sitePerformanceData} innerRadius={55} outerRadius={75} paddingAngle={8} dataKey="value" stroke="none">
                    {sitePerformanceData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{borderRadius: '8px', border: 'none'}} />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{fontSize: '9px', fontWeight: 'bold', textTransform: 'uppercase', paddingTop: '10px'}} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-col justify-center gap-6 border-l border-slate-100 pl-12">
              <MetricItem label="System Uptime" value="99.9%" color={COLORS.teal} progress={99} />
              <MetricItem label="Response Time" value="1.2s" color={COLORS.purple} progress={75} />
              <MetricItem label="Resolution Rate" value="88%" color={COLORS.primary} progress={88} />
            </div>
          </div>

          <section className="mb-20">
            <div className="flex justify-between items-end mb-8">
              <h2 className="font-['Poppins'] text-sm font-bold uppercase tracking-widest text-slate-900">Active Exceptions ({filteredAlerts.length})</h2>
              <span className="text-[10px] font-bold text-primary flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500"></span>
                </span>
                LIVE FEED
              </span>
            </div>

            <div className="divide-y divide-slate-50 border-t border-slate-100">
              {filteredAlerts.length > 0 ? filteredAlerts.map((alert) => (
                <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={alert.id} className="group py-5 flex items-center justify-between hover:bg-slate-50/50 transition-all px-4 rounded-xl">
                  <div className="flex items-center gap-6">
                    <div className="text-slate-300 group-hover:text-primary transition-colors text-lg">{alert.icon}</div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{alert.type}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{alert.emp} • {alert.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8">
                    <div className="flex items-center gap-2">
                      <FaCircle size={6} style={{ color: SEVERITY_COLORS[alert.sev] }} />
                      <span className="text-[10px] font-black uppercase tracking-tighter" style={{ color: SEVERITY_COLORS[alert.sev] }}>{alert.sev}</span>
                    </div>
                    <FaChevronRight size={10} className="text-slate-200 group-hover:text-slate-400 transition-colors" />
                  </div>
                </motion.div>
              )) : (
                <div className="py-20 text-center">
                  <p className="text-slate-400 text-xs font-medium italic">No active exceptions matching your filter.</p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const MetricItem = ({ label, value, color, progress }) => (
  <div className="w-full">
    <div className="flex justify-between items-end mb-2">
      <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{label}</span>
      <span className="text-sm font-bold text-slate-900">{value}</span>
    </div>
    <div className="h-1 w-full bg-slate-50 rounded-full overflow-hidden">
      <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{duration: 1}} className="h-full rounded-full" style={{ backgroundColor: color }} />
    </div>
  </div>
);

const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-8">
    <div className="grid grid-cols-3 gap-8">
      {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-50 rounded-2xl" />)}
    </div>
    <div className="h-64 bg-slate-100/50 rounded-2xl" />
  </div>
);

export default AlertsMonitor;